/* Auswertungen über den Demo-Zustand. Reine Funktionen, keine Oberfläche. */

export const POSITIONEN = ["dafuer", "dagegen", "unentschieden"];

export const POSITION_TEXT = {
  dafuer: "Dafür",
  dagegen: "Dagegen",
  unentschieden: "Unentschieden",
};

/*
  Die Abstimmungsampel: grün für Zustimmung, gelb für Enthaltung, rot für
  Ablehnung. So zeigen es Ratssäle und Ratsinformationssysteme — das
  Zeichensystem ist älter und stärker als jede Parteizuordnung.

  Rot-Grün ist zugleich die häufigste Farbfehlsichtigkeit. Deshalb steht
  überall, wo die Ampel auftaucht, zusätzlich eine Zahl, ein Zeichen oder
  das Wort. Die Farbe beschleunigt das Erfassen, sie trägt es nicht.
*/
export const AMPEL = [
  { position: "dafuer", ampel: "ja", kurz: "Dafür", zeichen: "+" },
  { position: "unentschieden", ampel: "enthaltung", kurz: "Unentschieden", zeichen: "?" },
  { position: "dagegen", ampel: "nein", kurz: "Dagegen", zeichen: "−" },
];

/*
  Der Name zu einer Kennung.

  Seit die Mitglieder echte Konten sind, kann eine Kennung ins Leere zeigen:
  Wer die Fraktion verlässt, verschwindet aus der Mitgliederliste — seine
  Beiträge und Markierungen bleiben aber stehen. Ein Absturz an dieser Stelle
  wäre die schlechteste aller Antworten.
*/
export function nameVon(stammdaten, personId, ichId) {
  if (personId && personId === ichId) return "Du";
  const person = (stammdaten.personen || {})[personId];
  return person ? person.name : "Ehemaliges Mitglied";
}

/* Wer sich wie geäußert hat — und wer noch gar nicht. */
/* „Renate Buschmann“ → „RB“. „Du“ bleibt „Du“ — das liest sich besser als „D“. */
export function kuerzel(name) {
  if (!name || name === "Du") return name || "";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join("");
}

export function meinungsbildFuerTop(zustand, stammdaten, topId) {
  const eintraege = zustand.meinungsbilder.filter((m) => m.topId === topId);
  const nachPosition = { dafuer: [], dagegen: [], unentschieden: [] };
  eintraege.forEach((m) => {
    if (nachPosition[m.position]) nachPosition[m.position].push(m.personId);
  });

  const geaeussert = new Set(eintraege.map((m) => m.personId));
  /*
    Offen ist, wer sich noch nicht geäußert hat. Gefragt sind alle Mitglieder
    der Fraktion — seit sie echte Konten sind, ist das schlicht jeder, der
    angemeldet ist und zur Fraktion gehört. Die Zugehörigkeit zu einzelnen
    Ausschüssen pflegt heute niemand, also darf sie hier auch nichts filtern.
  */
  const offen = Object.values(stammdaten.personen || {})
    .filter((p) => !geaeussert.has(p.id))
    .map((p) => p.id);

  return { ...nachPosition, offen, anzahlGeaeussert: geaeussert.size };
}

export function meineMeinung(zustand, ichId, topId) {
  const eintrag = zustand.meinungsbilder.find((m) => m.topId === topId && m.personId === ichId);
  return eintrag ? eintrag.position : null;
}

export function meineTeilnahme(zustand, ichId, sitzungId) {
  const eintrag = zustand.teilnahmen.find(
    (t) => t.sitzungId === sitzungId && t.personId === ichId
  );
  return eintrag ? eintrag.status : null;
}

export function teilnahmeZaehlung(zustand, sitzungId) {
  const eintraege = zustand.teilnahmen.filter((t) => t.sitzungId === sitzungId);
  return {
    ja: eintraege.filter((t) => t.status === "ja").length,
    nein: eintraege.filter((t) => t.status === "nein").length,
    unklar: eintraege.filter((t) => t.status === "unklar").length,
    offen: eintraege.filter((t) => t.status === null).length,
  };
}

/* Markierungen, die der Nutzer sehen darf: eigene plus die der Fraktion. */
export function sichtbareAnnotationen(zustand, ichId, dateiId) {
  return zustand.annotationen.filter(
    (a) =>
      a.dateiId === dateiId &&
      (a.sichtbarkeit === "fraktion" || a.verfasserId === ichId)
  );
}

/*
  Die Notizen zu einer Vorlage — also die Markierungen, an denen jemand
  etwas an den Rand geschrieben hat.

  Eine Markierung ohne Text ist keine Notiz: Sie sagt nur „hier will ich noch
  einmal hinschauen“ und richtet sich an niemanden. Nur was geschrieben wurde,
  steht in der Liste.

  Sortiert wird nach der Reihenfolge im Dokument, nicht nach der Zeit. Wer die
  Notizen der Fraktion durchgeht, liest sie neben dem Text — und der Text
  läuft von oben nach unten. Die Absatzkennungen sind gleich lang
  (`d184-06`, `d184-10`), deshalb sortiert der einfache Textvergleich richtig.
*/
export function notizenZuDatei(zustand, ichId, dateiId) {
  if (!dateiId) return [];
  return sichtbareAnnotationen(zustand, ichId, dateiId)
    .filter((a) => a.notiz && a.notiz.trim())
    .sort((x, y) => {
      const absatz = String(x.absatzId).localeCompare(String(y.absatzId));
      return absatz !== 0 ? absatz : (x.start ?? 0) - (y.start ?? 0);
    });
}

/* Zu welchem Tagesordnungspunkt gehört eine Datei? */
export function topZuDatei(stammdaten, dateiId) {
  const vorlage = Object.values(stammdaten.vorlagen).find((v) => v.dateiId === dateiId);
  if (!vorlage) return null;
  return (
    stammdaten.tagesordnung.find(
      (t) =>
        t.vorlageId === vorlage.id || (t.weitereVorlagenIds || []).includes(vorlage.id)
    ) || null
  );
}

/*
  Fassungen einer Vorlage.

  Die Verwaltung tauscht Vorlagen nach. Für die Fraktion ist das der Fall, der
  am meisten Arbeit vernichtet: Markierungen und Chatbeiträge beziehen sich auf
  Textstellen, die es in der neuen Fassung so nicht mehr gibt. Deshalb wird
  nichts umgehängt und nichts geraten — die alte Fassung bleibt mit allem
  stehen, und die Anwendung sagt, was wo liegt.

  Gibt null zurück, wenn es zu dieser Vorlage nur eine Fassung gibt.
*/
export function fassungslage(stammdaten, zustand, ichId, vorlage) {
  if (!vorlage) return null;

  const andere = vorlage.vorgaengerId
    ? stammdaten.vorlagen[vorlage.vorgaengerId]
    : vorlage.nachfolgerId
      ? stammdaten.vorlagen[vorlage.nachfolgerId]
      : null;
  if (!andere) return null;

  const istNeuere = Boolean(vorlage.vorgaengerId);
  const markierungen = andere.dateiId
    ? sichtbareAnnotationen(zustand, ichId, andere.dateiId).length
    : 0;

  /*
    Beiträge hängen am Tagesordnungspunkt, nicht an der Datei. Gezählt wird
    deshalb, was sich auf eine Markierung in der anderen Fassung bezieht —
    das ist der Teil, der beim Fassungswechsel ins Leere zeigen würde.
  */
  const bezogeneIds = new Set(
    zustand.annotationen.filter((a) => a.dateiId === andere.dateiId).map((a) => a.id)
  );
  const beitraege = zustand.nachrichten.filter(
    (n) => n.annotationId && bezogeneIds.has(n.annotationId)
  ).length;

  return { istNeuere, andere, markierungen, beitraege };
}

/* ---------------------------------------------------------------
   Kanäle

   Ein Kanal je Gremium, nicht je Tagesordnungspunkt. Die Begründung steht
   in KOMMUNIKATION.md; kurz: Ein Ausschuss besteht die ganze Wahlperiode,
   seine Themen kommen wieder, und dieselben Leute sitzen darin.
   --------------------------------------------------------------- */

export function nachrichtenZuKanal(zustand, kanalId) {
  return zustand.nachrichten.filter((n) => n.kanalId === kanalId);
}

/*
  Ungelesen sind Beiträge *anderer*, die nach dem letzten Öffnen des Kanals
  geschrieben wurden. Eigene Beiträge zählen nie — man hat sie ja selbst
  getippt, und eine Marke, die einen an die eigenen Worte erinnert, macht die
  Liste nur unbrauchbar.

  Wer einen Kanal noch nie geöffnet hat, hat alles noch vor sich. Das ist
  gewollt: „Ungelesen“ ist die Antwort auf „was ist neu für mich“, und für
  einen ungeöffneten Kanal lautet sie „alles“.
*/
export function ungeleseneImKanal(zustand, kanalId, ichId) {
  const seit = zustand.kanalGelesen[kanalId];
  const grenze = seit ? new Date(seit) : null;

  return nachrichtenZuKanal(zustand, kanalId).filter(
    (n) => n.verfasserId !== ichId && (!grenze || new Date(n.zeit) > grenze)
  ).length;
}

export function istKanalStumm(zustand, kanalId) {
  return Boolean(zustand.kanalStumm[kanalId]);
}

/*
  Die Kanalliste, wie sie auf dem Bildschirm steht.

  Sortiert nach dem letzten Beitrag — der lebendigste Kanal oben, wie in
  jedem Messenger. Ein stummer Kanal rutscht dabei nicht nach unten: Er ist
  leise, nicht unwichtig, und wer ihn sucht, sucht ihn dort, wo er zuletzt
  stand.
*/
export function kanaluebersicht(stammdaten, zustand, ichId) {
  return (stammdaten.kanaele || [])
    .map((kanal) => {
      const beitraege = nachrichtenZuKanal(zustand, kanal.id);
      return {
        kanal,
        beitraege,
        letzter: beitraege.length ? beitraege[beitraege.length - 1] : null,
        ungelesen: ungeleseneImKanal(zustand, kanal.id, ichId),
        stumm: istKanalStumm(zustand, kanal.id),
      };
    })
    .sort((a, b) => {
      if (!a.letzter && !b.letzter) return 0;
      if (!a.letzter) return 1;
      if (!b.letzter) return -1;
      return new Date(b.letzter.zeit) - new Date(a.letzter.zeit);
    });
}

/*
  In welchem Kanal wird ein Tagesordnungspunkt besprochen? Im Kanal des
  Gremiums, das darüber berät. Nur dort — ein Punkt hat keinen eigenen Kanal
  mehr.
*/
export function kanalZuTop(stammdaten, top) {
  if (!top) return "k-fraktion";
  return (stammdaten.kanalDerSitzung || {})[top.sitzungId] || "k-fraktion";
}

/* Wie viele ungelesene Beiträge hat die Fraktion insgesamt — ohne stumme. */
export function ungeleseneGesamt(stammdaten, zustand, ichId) {
  return kanaluebersicht(stammdaten, zustand, ichId)
    .filter((k) => !k.stumm)
    .reduce((summe, k) => summe + k.ungelesen, 0);
}

export function fragenZuTop(stammdaten, topId) {
  return stammdaten.fragen.filter((f) => f.topId === topId);
}

export function istGelesen(zustand, dateiId) {
  return dateiId ? zustand.gelesen.includes(dateiId) : true;
}

/*
  Alles, was im Kalender einen Punkt bekommt.

  Zwei Quellen: die Sitzungen aus dem Ratsinformationssystem samt der
  Fraktionssitzung davor (`stammdaten.terminplan`) und die Termine, die die
  Fraktion selbst verabredet hat (`zustand.termine`).

  Sie werden hier zusammengeführt und nicht im Kalenderblatt: Das Blatt soll
  Punkte zeichnen und nicht wissen, woher sie kommen.
*/
export function kalendereintraege(stammdaten, zustand) {
  const ausSitzungen = stammdaten.terminplan || [];

  const ausTerminen = (zustand.termine || []).map((t) => ({
    id: t.id,
    art: "termin",
    name: t.titel,
    beginn: t.beginn,
    ort: t.ort,
    terminId: t.id,
  }));

  return [...ausSitzungen, ...ausTerminen].sort((a, b) => a.beginn - b.beginn);
}

/*
  Gehört eine Aufgabe in MEIN Brett?

  Zwei Angaben, und beide zählen: die **Verantwortung** — genau eine Person,
  die es tut — und die **Beobachtung** — beliebig viele, die es angeht. Wer
  eine Aufgabe anlegt, beobachtet sie automatisch; deshalb bleibt sie im
  eigenen Brett stehen, auch wenn man sie weitergibt.

  Anders als „wer hat sie angelegt“ ist die Beobachtung änderbar: Wer aus
  einer Sache raus ist, hakt sich aus.
*/
export function gehoertZuMir(aufgabe, ichId) {
  return (
    aufgabe.zustaendigId === ichId || (aufgabe.beobachterIds || []).includes(ichId)
  );
}

/*
  Was ich selbst zu tun habe — für die Startseite, die „Was muss ich bis zur
  nächsten Sitzung tun?“ beantwortet. Hier zählt nur die Zuständigkeit:
  Fremde Arbeit gehört nicht in die eigene Liste.
*/
export function offeneAufgaben(zustand, ichId) {
  return zustand.aufgaben
    .filter((a) => a.zustaendigId === ichId && !a.erledigt)
    .sort((a, b) => new Date(a.faellig) - new Date(b.faellig));
}

/*
  Der Bearbeitungsstand eines Tagesordnungspunkts aus Sicht des Nutzers.
  Grundlage für die Startseite und für die Leitfrage „Was muss ich noch tun?“.
*/
export function standDesTop(top, zustand, stammdaten, ichId) {
  const vorlage = top.vorlageId ? stammdaten.vorlagen[top.vorlageId] : null;
  const datei = vorlage ? stammdaten.dateien[vorlage.dateiId] : null;

  return {
    vorlage,
    datei,
    hatVorlage: Boolean(vorlage),
    gelesen: datei ? istGelesen(zustand, datei.id) : true,
    meineMeinung: meineMeinung(zustand, ichId, top.id),
    anzahlMarkierungen: datei ? sichtbareAnnotationen(zustand, ichId, datei.id).length : 0,
    anzahlFragen: fragenZuTop(stammdaten, top.id).length,
    meinungsbild: meinungsbildFuerTop(zustand, stammdaten, top.id),
  };
}

/*
  Die offenen Punkte bis zur nächsten Sitzung — die Antwort auf die Leitfrage
  der Startseite. Bewusst als Sätze, nicht als Kennzahlen-Kacheln.
*/
export function offenePunkte(zustand, stammdaten, ichId) {
  const inhaltlicheTops = stammdaten.tagesordnung.filter((t) => t.vorlageId);

  const ungelesen = inhaltlicheTops.filter((t) => {
    const vorlage = stammdaten.vorlagen[t.vorlageId];
    return vorlage && !istGelesen(zustand, vorlage.dateiId);
  });

  const ohneMeinung = inhaltlicheTops.filter(
    (t) => meineMeinung(zustand, ichId, t.id) === null
  );

  const aufgaben = offeneAufgaben(zustand, ichId);
  const teilnahmeFraktion = meineTeilnahme(zustand, ichId, "s-fraktion");

  return { ungelesen, ohneMeinung, aufgaben, teilnahmeOffen: teilnahmeFraktion === null };
}
